import { FC } from 'react';
import { Anchor, Badge, Group, Image, Paper, Stack, Text } from '@mantine/core';
import { IconArrowRight, IconCheck, IconExternalLink } from '@tabler/icons-react';
import * as Enums from '@/utils/enums';
import { ActiveIntegration } from '@/PageComponents/Onboarding/onboarding-rules';
import { ONBOARDING_PARTNERS } from '@/PageComponents/Onboarding/onboarding-config';
import InfoBadge from '@/PageComponents/Onboarding/components/InfoBadge';
import { SecondaryButton } from '@/PageComponents/Onboarding/components/OnboardingButtons';
import styles from '@/PageComponents/Onboarding/components/ImportDataSetup.module.css';

export const integrationStatusLabel = (status: number): string => {
    switch (status) {
        case Enums.IntegrationStatus.Live: return 'Syncing';
        case Enums.IntegrationStatus.Commissioning: return 'Setup incomplete';
        case Enums.IntegrationStatus.Unauthorized: return 'Needs attention';
        default: return 'Not connected';
    }
};

const statusColor = (status: number) =>
    status === Enums.IntegrationStatus.Live ? 'green' : status === Enums.IntegrationStatus.Unauthorized ? 'red' : 'yellow';

// The order the design shows the brand marks in.
const MARK_ORDER = [Enums.IntegrationPartner.Xero, Enums.IntegrationPartner.SageOne, Enums.IntegrationPartner.QuickBooks];
const marks = MARK_ORDER.map(p => ONBOARDING_PARTNERS.find(x => x.partner === p)).filter(Boolean) as typeof ONBOARDING_PARTNERS;

export interface ImportDataSetupProps {
    integration: ActiveIntegration | null;
    completedImportCount: number | null;
    /** Opens the "Connect your data" explainer ("Find out more"). */
    onLearnMore: () => void;
    /** Opens the integration settings page (in a new tab) to manage the account connected. */
    onManageIntegration: () => void;
    /** Opens the spreadsheet imports in settings. */
    onViewImports: () => void;
}

const ImportDataSetup: FC<ImportDataSetupProps> = ({ integration, completedImportCount, onLearnMore, onManageIntegration, onViewImports }) => {
    const activePartner = integration ? ONBOARDING_PARTNERS.find(p => p.partner === integration.Partner) : undefined;

    return (
        <Paper radius={4} className={styles.task}>
            <Stack gap={1}>
                <Text className={styles.title}>Import your data</Text>
                <Text className={styles.text}>ServCraft can sync your customers and inventory data if you have an existing account package.</Text>
            </Stack>

            {activePartner && integration ? (
                <Group gap="sm" mt={16} data-testid="onboarding-active-integration">
                    <Image src={activePartner.logoUrl} alt="" w={34} h={34} fit="contain" />
                    <Text fw={600}>{activePartner.name}</Text>
                    <Badge variant="light" color={statusColor(integration.Status)} radius="xl">{integrationStatusLabel(integration.Status)}</Badge>
                    <SecondaryButton id="onboarding-open-integration-settings" onClick={onManageIntegration} leftSection={<IconExternalLink size={18} />} className={styles.connect}>Open integration settings</SecondaryButton>
                </Group>
            ) : (
                <Group gap={20} mt={16} className={styles.connectRow}>
                    <Group gap={7} aria-label="Xero, Sage One and QuickBooks Online" role="img">
                        {marks.map(p => <Image key={p.partner} src={p.logoUrl} alt="" w={34} h={34} fit="contain" />)}
                    </Group>
                    <InfoBadge id="onboarding-find-out-more" label="Find out more" onClick={onLearnMore} />
                </Group>
            )}

            <Stack gap={6} mt={16}>
                <Text className={styles.text}>
                    <Text span fw={800} inherit>Don't have these accounting solutions? </Text>
                    No problem. Upload a spreadsheet of your customers and inventory.{' '}
                    <Anchor id="onboarding-manual-import" component="button" type="button" inherit className={styles.link} onClick={onViewImports}>
                        Try manual data import<IconArrowRight size={14} className={styles.linkArrow} />
                    </Anchor>
                </Text>
                {(completedImportCount ?? 0) > 0 && (
                    <Anchor id="onboarding-view-imports" component="button" type="button" size="sm" onClick={onViewImports} aria-label="Data imported — view imports" w="fit-content">
                        <Group gap={4}><IconCheck size={14} />Data imported<IconArrowRight size={14} /></Group>
                    </Anchor>
                )}
            </Stack>
        </Paper>
    );
};

export default ImportDataSetup;
